import React, { useState } from 'react';
import { Banner, Button, Heading, UtilityAlertIcon } from '@placer-ui/components';
import { AuthPageWrapper } from 'features/authentication/auth-page-wrapper/auth-page-wrapper';
import { SendEmailInputForm } from 'features/authentication/components/send-email-input-form/send-email-input-form';
import { goToSignIn } from 'router/go-to';
import {
    EmailDataFormParameters,
    SplunkReportConfig,
} from 'store/auth/actions/auth-actions/auth-action-types';
import { useForgotPasswordForm } from 'features/authentication/forgot-password/utils';
import {
    BACKEND_DEFAULT_CODE,
    BACKEND_SUCCESS_CODE,
    forgotPasswordBaseSplunkData,
} from 'features/authentication/utils/constants';
import { useSplunkCallback } from 'hooks/use-splunk-callback/use-splunk-callback';
import { EmailSentPage } from 'features/authentication/forgot-password/email-sent-page/email-sent-page';
import { heapIdentify, heapTrack } from 'utils/heap/heap';
import { splunkEventsData } from 'features/authentication/forgot-password/constants';
import {
    EmailFormErrorCodeMessages,
    sendEmailErrorMessagesText,
} from 'features/authentication/utils/utils';
import styles from './forgot-password.module.scss';

export const ForgotPassword = () => {
    const [emailSent, setEmailSent] = useState(false);
    const { sendEmail } = useForgotPasswordForm();
    const sendSplunkEvent = useSplunkCallback({});
    const [errorCode, setErrorCode] = useState<number>(BACKEND_DEFAULT_CODE);

    const onSendEmail = (formParameters: EmailDataFormParameters) => {
        setErrorCode(BACKEND_DEFAULT_CODE);
        heapIdentify(formParameters.email);
        sendSplunkEvent({
            ...forgotPasswordBaseSplunkData,
            action: 'send',
            value: formParameters.email,
        });
        return sendEmail(formParameters);
    };

    const onSendEmailFailure = (email: string, reportConfig: SplunkReportConfig) => {
        setErrorCode(reportConfig.statusCode);
        heapTrack('Forgot Password - email sending failed', reportConfig);
        sendSplunkEvent({
            ...forgotPasswordBaseSplunkData,
            action: 'send-fail',
            value: email,
            report_config: reportConfig,
        });
    };

    const onSuccess = (email: string) => {
        setErrorCode(BACKEND_DEFAULT_CODE);
        heapTrack('Forgot Password - Email sent successfully');
        setEmailSent(true);
        sendSplunkEvent({
            ...forgotPasswordBaseSplunkData,
            action: 'send-success',
            value: email,
            report_config: {
                statusCode: BACKEND_SUCCESS_CODE,
            },
        });
    };

    const inactiveAccountSplunkEventData = {
        ...forgotPasswordBaseSplunkData,
        action: splunkEventsData.emailNotAssociatedError,
    };

    const emailFormPageComponent = (
        <div className={styles.forgotPasswordScreenContainer}>
            <div className={styles.title}>
                <Heading variant="h3">Reset Your Password</Heading>
            </div>
            <SendEmailInputForm
                sendEmail={onSendEmail}
                onSuccessCallback={onSuccess}
                onFailureCallback={onSendEmailFailure}
                inactiveAccountSplunkEventParams={inactiveAccountSplunkEventData}
                submitButtonLabel="Get Access"
            />
            <div className={styles.backButton}>
                <Button
                    testId="forgot-password-back-to-sign-in"
                    fullWidth
                    variant="text"
                    onClick={() => goToSignIn()}
                >
                    Back
                </Button>
            </div>
        </div>
    );

    return (
        <AuthPageWrapper>
            <div className={styles.pageContainer}>
                {errorCode in EmailFormErrorCodeMessages && (
                    <Banner
                        LeftIcon={<UtilityAlertIcon />}
                        text={sendEmailErrorMessagesText[errorCode]}
                        type="error"
                    />
                )}
                {!emailSent ? emailFormPageComponent : <EmailSentPage />}
            </div>
        </AuthPageWrapper>
    );
};
